import { Exchange, type IncrementalTicker } from '@/types/generated/common/types'

/**
 * Venues whose option tickers carry no `underlyingPrice` (a Deribit concept) but whose
 * `indexPrice` supplies the underlying. OPRA is priced at r = 0, so the forward is the ETF's last
 * trade and `indexPrice` already carries it (ENG-1064/ENG-1077).
 * - Not Deribit: there `indexPrice` is the spot index and `underlyingPrice` the per-expiry
 *   forward, so falling back would flip ATM/ITM shading whenever a batch lacked the forward.
 * - GLBX also uses indexPrice when its option tickers omit underlyingPrice.
 */
const INDEX_AS_UNDERLYING_EXCHANGES: ReadonlySet<Exchange> = new Set([
  Exchange.EXCHANGE_OPRA,
  Exchange.EXCHANGE_GLBX,
])

function positivePrice(value: string | undefined): number | null {
  if (!value) return null
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? n : null
}

/**
 * The option chain's underlying price (ATM indicator, ITM shading, ATM scroll) from a batch
 * of merged tickers: the first `underlyingPrice`, else — on venues that never send one —
 * the first `indexPrice`. Null when the batch has neither, so the caller keeps its last value.
 */
export function resolveChainUnderlyingPrice(
  tickers: Iterable<IncrementalTicker>,
  exchange: Exchange
): number | null {
  const list = [...tickers]
  for (const t of list) {
    const price = positivePrice(t.underlyingPrice)
    if (price != null) return price
  }
  if (!INDEX_AS_UNDERLYING_EXCHANGES.has(exchange)) return null
  for (const t of list) {
    const price = positivePrice(t.indexPrice)
    if (price != null) return price
  }
  return null
}
